import { type Static, Type } from "@sinclair/typebox";
import { StringEnum } from "../typebox.ts";
import {
  ACCESSORY_IDS,
  BACKGROUND_IDS,
  EAR_IDS,
  EYE_COLOR_IDS,
  EYE_IDS,
  FACE_SHAPE_IDS,
  FUR_COLOR_IDS,
  MOUTH_IDS,
  PATTERN_IDS,
  WHISKER_IDS,
} from "./parts.ts";

/**
 * アバターは「パーツ ID の組み合わせ」として保存する (posts.author_avatar / users.avatar は jsonb)。
 * `v` はカタログの世代。パーツを非互換に変更するときに上げてマイグレーションの目印にする。
 */
export const AvatarSchema = Type.Object(
  {
    v: Type.Literal(1),
    shape: StringEnum(FACE_SHAPE_IDS),
    furColor: StringEnum(FUR_COLOR_IDS),
    pattern: StringEnum(PATTERN_IDS),
    ears: StringEnum(EAR_IDS),
    eyes: StringEnum(EYE_IDS),
    eyeColor: StringEnum(EYE_COLOR_IDS),
    mouth: StringEnum(MOUTH_IDS),
    whiskers: StringEnum(WHISKER_IDS),
    accessory: StringEnum(ACCESSORY_IDS),
    background: StringEnum(BACKGROUND_IDS),
  },
  { additionalProperties: false, $id: "Avatar" },
);

export type Avatar = Static<typeof AvatarSchema>;

export const DEFAULT_AVATAR: Avatar = {
  v: 1,
  shape: "round",
  furColor: "cream",
  pattern: "tabby",
  ears: "pointed",
  eyes: "round",
  eyeColor: "amber",
  mouth: "smile",
  whiskers: "gentle",
  accessory: "none",
  background: "none",
};

function pick<T>(items: readonly T[], n: number): T {
  const value = items[Math.abs(n) % items.length];
  if (value === undefined) throw new Error("avatar catalog is empty");
  return value;
}

/** FNV-1a。seed から決定的にアバターを作るためだけのハッシュ。 */
function hash(seed: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/**
 * seed (user id 等) から決定的にアバターを組み立てる。
 * ゲストが何も選ばなくても毎回同じ猫が出るようにするために使う。
 */
export function avatarFromSeed(seed: string): Avatar {
  const h = hash(seed);
  const at = (shift: number) => (h >>> shift) ^ (h * (shift + 1));
  return {
    v: 1,
    shape: pick(FACE_SHAPE_IDS, at(0)),
    furColor: pick(FUR_COLOR_IDS, at(3)),
    pattern: pick(PATTERN_IDS, at(6)),
    ears: pick(EAR_IDS, at(9)),
    eyes: pick(EYE_IDS, at(12)),
    eyeColor: pick(EYE_COLOR_IDS, at(15)),
    mouth: pick(MOUTH_IDS, at(18)),
    whiskers: pick(WHISKER_IDS, at(21)),
    accessory: pick(ACCESSORY_IDS, at(24)),
    background: pick(BACKGROUND_IDS, at(27)),
  };
}

/** ビルダーの「おまかせ」ボタン用。 */
export function randomAvatar(): Avatar {
  return avatarFromSeed(`${Date.now()}:${Math.random()}`);
}
